import Subheadline from "../Elements/Subheadline";
import ProfileCard from "../Cards/CardProfile";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import InputText from "../Inputs/InputText";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuthContext } from "../../context/context";
import { useLanguage } from "../../context/LanguageContext";
import { updatePassword } from "../../api";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

const TabSecurity = ({ setToast }) => {
  const { userData, logout } = useAuthContext();
  const { copy } = useLanguage();
  const navigate = useNavigate();

  // Password form schema
  const passwordSchema = userData?.hasPassword
    ? z
        .object({
          password: z
            .string()
            .min(12, copy.profile.errors.current_password_required),
          new_password: z
            .string()
            .min(12, copy.profile.errors.new_password_required),
          confirm_password: z
            .string()
            .min(12, copy.profile.errors.confirm_password_required),
        })
        .refine((data) => data.new_password === data.confirm_password, {
          message: copy.register.errors.passwords_do_not_match,
          path: ["confirm_password"],
        })
    : z
        .object({
          new_password: z
            .string()
            .min(12, copy.profile.errors.new_password_required),
          confirm_password: z
            .string()
            .min(12, copy.profile.errors.confirm_password_required),
        })
        .refine((data) => data.new_password === data.confirm_password, {
          message: copy.register.errors.passwords_do_not_match,
          path: ["confirm_password"],
        });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const handleUpdatePassword = async (data) => {
    const userId = userData?.id;
    if (!userId)
      return setToast({
        message: copy.profile.errors.user_id_missing,
        type: "error",
      });
    try {
      const response = await updatePassword(userId, data);
      console.log("Password update response:", response);

      reset();

      await logout(
        `/login?password${userData?.hasPassword ? "Updated" : "Added"}=true`
      );
    } catch (error) {
      console.log("Password update error:", error);
      setToast({
        message: copy.profile.errors.password_update_failed,
        type: "error",
      });
    }
  };

  return (
    <>
      <Subheadline
        subheadline={copy.profile.tab_3}
        description={copy.profile.tab_3_description}
      />
      <div className="flex flex-col gap-3">
        <ProfileCard
          title={
            userData?.hasPassword
              ? copy.profile.cards.security.update_password_title
              : copy.profile.cards.security.add_password_title
          }
        >
          <form
            className="flex flex-col gap-4 mt-4 w-80"
            onSubmit={handleSubmit(handleUpdatePassword)}
          >
            {userData?.hasPassword && (
              <InputText
                label={copy.profile.form.current_password}
                type="password"
                {...register("password")}
                error={errors.password?.message}
              />
            )}
            <InputText
              label={copy.profile.form.new_password}
              type="password"
              {...register("new_password")}
              error={errors.new_password?.message}
              details={copy.register.form.details_password}
            />
            <InputText
              label={copy.profile.cards.security.confirm_new_password}
              type="password"
              {...register("confirm_password")}
              error={errors.confirm_password?.message}
              details={copy.register.form.details_password}
            />
            <div>
              <BtnSecondaryM
                type="submit"
                buttonCTA={
                  userData?.hasPassword
                    ? copy.profile.form.button_update_password
                    : copy.profile.cards.security.add_password_title
                }
              />
            </div>
          </form>
        </ProfileCard>
      </div>
    </>
  );
};

TabSecurity.propTypes = {
  setToast: PropTypes.func.isRequired,
};

export default TabSecurity;
